import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import { useAuthStore } from '../stores/authStore';

/**
 * Only rendered while this tab holds an impersonated session. Sits above the
 * whole layout so it cannot be scrolled away.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation('common');
  const impersonatedBy = useAuthStore((s) => s.impersonatedBy);
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  if (!impersonatedBy) return null;

  const exit = () => {
    clearAuth();
    window.close();
    // Browsers may refuse to close a tab they did not open by script. Say so
    // rather than leaving a signed-out shell that looks broken.
    window.location.replace('/impersonate/ended');
  };

  return (
    // Centred, not space-between: pushing Thoát to the far edge put it half a
    // metre from the sentence it belongs to on a wide monitor, so the two read
    // as unrelated. Keeping them adjacent is also what makes the exit findable.
    //
    // The colours are `attention-tint` / `attention-foreground` because that is
    // what the scale actually defines (see packages/ui/src/global.css) — this
    // banner shipped with `bg-attention-100` and `text-attention-900`, which
    // are not tokens, so Tailwind emitted nothing and the bar rendered
    // transparent. Same palette as CloseMonthPage's warning strip.
    <div className="flex shrink-0 flex-wrap items-center justify-center gap-x-2.5 gap-y-1 border-b border-attention/30 bg-attention-tint px-4 py-2 text-center text-sm text-attention-foreground">
      <span className="min-w-0">
        {t('impersonation.viewingAs', { name: user?.fullName || user?.email, org: organization?.name })}
        {' · '}
        <Popover>
          <PopoverTrigger className="underline decoration-dashed underline-offset-4">
            {t('impersonation.readOnly')}
          </PopoverTrigger>
          <PopoverContent align="center" className="w-80 space-y-2 text-xs leading-relaxed">
            <p className="font-medium">{t('impersonation.popover.title')}</p>
            <p>{t('impersonation.popover.writes')}</p>
            <p>{t('impersonation.popover.pages')}</p>
            <p>{t('impersonation.popover.sessions')}</p>
            <p>{t('impersonation.popover.expiry')}</p>
            <p>{t('impersonation.popover.audit')}</p>
          </PopoverContent>
        </Popover>
      </span>
      <button type="button" onClick={exit} className="shrink-0 font-semibold underline underline-offset-4">
        {t('impersonation.exit')}
      </button>
    </div>
  );
}
